import {filter, isArray} from 'lodash';
import {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {MediaDevicesConstraints} from 'types';
import {logger, mediaDevicesUtils} from 'utils';

export type UseLocalStreamsOptions = {
    enabled?: boolean;
    mediaConstraints: MediaDevicesConstraints;
    onEnumerateMediaDevices?: (devices: MediaDeviceInfo[]) => void;
    onEnumerateTracks?: (tracks: MediaStreamTrack[]) => void;
    onSuccess?: () => void;
    onError?: (error: Error) => void;
};

export type UseLocalStreamsReturn = {
    tracks: MediaStreamTrack[];
    audioTrack?: MediaStreamTrack;
    devices: MediaDeviceInfo[];
    isLoading: boolean;
    error: Error | null;
    stream: MediaStream | null;
    retryConnection: () => Promise<void>;
};

export function useLocalStreams({
    enabled = true,
    mediaConstraints,
    onEnumerateMediaDevices,
    onEnumerateTracks,
    onSuccess,
    onError
}: UseLocalStreamsOptions): UseLocalStreamsReturn {
    const [tracks, setTracks] = useState<MediaStreamTrack[]>([]);
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);

    const streamRef = useRef<MediaStream | null>(null);
    const {t} = useTranslation();
    const getUserMediaRef = useRef<(() => Promise<void>) | null>(null);
    const userStreamTracksRef = useRef<MediaStreamTrack[]>([]);
    const lastTracksIds = useRef<string>('');
    const mediaConstraintsRef = useRef(mediaConstraints);
    const callbacksRef = useRef({
        onEnumerateMediaDevices,
        onEnumerateTracks,
        onSuccess,
        onError
    });

    const audioTrack = useMemo(() => filter(tracks, (track) => track.kind === 'audio')?.[0], [tracks]);

    mediaConstraintsRef.current = mediaConstraints;
    callbacksRef.current = {
        onEnumerateMediaDevices,
        onEnumerateTracks,
        onSuccess,
        onError
    };

    const updateTracks = useCallback((newTracks: MediaStreamTrack[]) => {
        const newTracksIds = newTracks.map((track) => track.id).join(',');
        if (newTracksIds !== lastTracksIds.current) {
            lastTracksIds.current = newTracksIds;
            setTracks(newTracks);
            callbacksRef.current.onEnumerateTracks?.(newTracks);
        }
    }, []);

    const getUserMedia = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const constraints = mediaConstraintsRef.current;
            const needsAudio = Boolean(constraints?.audioinput);
            const needsVideo = Boolean(constraints?.videoinput);

            if (!needsAudio && !needsVideo) {
                const deviceList = await navigator.mediaDevices.enumerateDevices();
                const filteredDevices = mediaDevicesUtils.getMediaDevices(deviceList, t);

                setDevices(filteredDevices);
                callbacksRef.current.onEnumerateMediaDevices?.(filteredDevices);
                updateTracks([]);
                setIsLoading(false);
                callbacksRef.current.onSuccess?.();
                return;
            }

            const streamConstraints: MediaStreamConstraints = {};

            if (needsAudio) {
                if (typeof constraints.audioinput === 'boolean') {
                    streamConstraints.audio = {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    };
                } else if (constraints.audioinput && typeof constraints.audioinput === 'object') {
                    // For Safari, ensure proper audio constraints
                    const audioConstraints: any = {...constraints.audioinput};

                    // Safari specific adjustments
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    if (isSafari) {
                        // Remove unsupported constraints for Safari
                        delete audioConstraints.autoGainControl;
                        delete audioConstraints.noiseSuppression;

                        // Ensure echoCancellation is properly set
                        if (!('echoCancellation' in audioConstraints)) {
                            audioConstraints.echoCancellation = true;
                        }
                    } else {
                        if (!('echoCancellation' in audioConstraints)) {
                            audioConstraints.echoCancellation = true;
                        }
                        if (!('noiseSuppression' in audioConstraints)) {
                            audioConstraints.noiseSuppression = true;
                        }
                        if (!('autoGainControl' in audioConstraints)) {
                            audioConstraints.autoGainControl = true;
                        }
                    }

                    streamConstraints.audio = audioConstraints;
                } else {
                    streamConstraints.audio = {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    };
                }
            }

            if (needsVideo) {
                streamConstraints.video = constraints.videoinput || true;
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    track.stop();
                });
                streamRef.current = null;
                setStream(null);
            }

            const userStream = await navigator.mediaDevices.getUserMedia(streamConstraints);

            let finalStream = userStream;

            userStreamTracksRef.current = userStream.getAudioTracks();
            if (needsAudio && userStreamTracksRef.current?.length > 0) {
                try {
                    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                    await audioContext.resume();
                    audioContextRef.current = audioContext;

                    const micSource = audioContext.createMediaStreamSource(userStream);
                    const micDestination = audioContext.createMediaStreamDestination();

                    const gainNode = audioContext.createGain();
                    gainNode.gain.value = 0;

                    micSource.connect(micDestination);
                    micSource.connect(gainNode);
                    gainNode.connect(audioContext.destination);

                    finalStream = micDestination.stream;
                } catch (err) {
                    logger.warn('AudioContext isolation failed', err);
                }
            }

            streamRef.current = finalStream;
            setStream(finalStream);

            const deviceList = await navigator.mediaDevices.enumerateDevices();
            const filteredDevices = mediaDevicesUtils.getMediaDevices(deviceList, t);

            const streamTracks = finalStream.getTracks();

            setDevices(filteredDevices);
            updateTracks(streamTracks);

            callbacksRef.current.onEnumerateMediaDevices?.(filteredDevices);
            callbacksRef.current.onSuccess?.();

            logger.debug('Local streams initialized', {
                tracks: streamTracks.length,
                devices: filteredDevices.length,
                audio: needsAudio,
                video: needsVideo
            });

        } catch (err: any) {
            const errorObj = new Error(`Failed to access media devices: ${err.message}`);
            setError(errorObj);
            callbacksRef.current.onError?.(errorObj);

            logger.error('getUserMedia failed', {
                error: err.message,
                name: err.name,
                constraint: err.constraint
            });
        } finally {
            setIsLoading(false);
        }
    }, [t, updateTracks]);

    getUserMediaRef.current = getUserMedia;

    const retryConnection = useCallback(() => {
        setError(null);
        return getUserMediaRef.current?.();
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        getUserMediaRef.current?.();

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    track.stop();
                });
                streamRef.current = null;
            }

            if (isArray(userStreamTracksRef.current)) {
                userStreamTracksRef.current.forEach((track) => {
                    track.stop();
                });
                userStreamTracksRef.current = [];
            }
        };
    }, [enabled]);

    useEffect(() => {
        const prevConstraints = JSON.stringify(mediaConstraintsRef.current);
        const newConstraints = JSON.stringify(mediaConstraints);

        if (prevConstraints !== newConstraints) {
            getUserMediaRef.current?.();
        }
    }, [mediaConstraints]);

    useEffect(() => {
        const handleDeviceChange = () => {
            logger.debug('Media devices changed, re-enumerating...');
            getUserMediaRef.current?.();
        };

        navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);

        return () => {
            navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
        };
    }, []);

    useEffect(() => () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
    }, []);

    return {
        audioTrack,
        tracks,
        devices,
        isLoading,
        error,
        stream,
        retryConnection
    };
}